import { ExternalLink, Plus } from "lucide-react";
import React from "react";
import { useGenerateAccessTicket, useSSOConnections } from "../../../api/sso";
import { Alert, AlertDescription, AlertTitle } from "../../ui/alert";
import { Button } from "../../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";

import { useOrgUsers } from "@/api/users";
import { config } from "@/config";
import { useSettingsInDialog } from "@/context/SettingsInDialogContext";
import { formatProvider, getProviderIcon } from "@/utils/authProvider";

const OrgSSO: React.FC = () => {
  const isInDialog = useSettingsInDialog();
  const { data: users } = useOrgUsers();

  const {
    data: connections = [],
    isLoading,
    error: queryError,
  } = useSSOConnections();

  const { mutate: generateTicket, isPending } = useGenerateAccessTicket({
    onSuccess: (data) => {
      window.open(data.ticket_url, "_blank");
    },
  });

  const handleSetupNewSSO = () => {
    generateTicket(undefined);
  };

  const handleManageConnection = (connectionId: string) => {
    generateTicket(connectionId);
  };

  if (isLoading) {
    return null;
  }

  return (
    <>
      <title>Single Sign-On</title>
      {isInDialog && connections.length === 0 ? (
        <div className="flex items-center justify-end mb-4">
          <Button
            onClick={handleSetupNewSSO}
            disabled={isPending}
            isLoading={isPending}
          >
            <Plus className="h-4 w-4" />
            Add SSO Connection
          </Button>
        </div>
      ) : null}

      <div className="space-y-6">
        {queryError && connections.length === 0 && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Failed to load SSO connections. Please try again.
            </AlertDescription>
          </Alert>
        )}

        {connections.length === 0 ? (
          <Card>
            <CardHeader>
              <CardDescription>
                Set up Single Sign-On (SSO) to allow your team members to sign
                in using your organization's identity provider.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                <p>The following providers are supported:</p>
                <ul className="list-disc list-inside mt-2 ml-2 space-y-1">
                  <li>Okta Workforce Identity Cloud (using OIDC)</li>
                  <li>Entra ID</li>
                  <li>Google Workspace (using OIDC)</li>
                  <li>Keycloak</li>
                  <li>Microsoft Active Directory Federation Services (ADFS)</li>
                  <li>PingFederate</li>
                  <li>Generic OIDC</li>
                  <li>Generic SAML</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {connections.map((connection) => {
              const providerIcon = getProviderIcon(connection.strategy);

              const numberUsers =
                users?.filter((user) => user.provider === connection.strategy)
                  ?.length || 0;

              return (
                <div>
                  <Card key={connection.id}>
                    <CardHeader className="pb-3">
                      <div className="sm:flex justify-between items-center">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            {providerIcon && (
                              <>
                                {typeof providerIcon === "string" ? (
                                  <img
                                    src={config.hosts.static + providerIcon}
                                    alt="Provider"
                                    className="h-8 w-8 relative top-[-1px]"
                                  />
                                ) : (
                                  React.createElement(providerIcon, {
                                    className: "h-7 w-7",
                                  })
                                )}
                              </>
                            )}
                            {formatProvider(connection.strategy)}
                          </CardTitle>
                        </div>

                        <div className="mt-5 sm:mt-0 flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              handleManageConnection(connection.id)
                            }
                            disabled={isPending}
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Manage
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center space-x-2">
                        <div className={`h-2 w-2 rounded-full bg-lc-active`} />
                        <span className="text-sm capitalize">Active</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-2">
                        {numberUsers} user
                        {numberUsers === 1 ? " has" : "s have"} logged in using
                        this connection
                      </div>
                    </CardContent>
                  </Card>
                  <div className="flex justify-end">
                    <p className="text-xs text-muted-foreground mt-2 ml-auto">
                      To remove this SSO connection, please email{" "}
                      <a
                        href="mailto:support@cohesive.ai"
                        className="underline"
                      >
                        support@cohesive.ai
                      </a>
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default OrgSSO;
